import { createContext, useContext, useCallback, useMemo, type ReactNode } from 'react'
import { useWorkspace } from './WorkspaceContext'
import { useAppSelector, useAppDispatch } from '../store/hooks'
import {
    createTaskSaga,
    createTasksSaga,
    updateTaskSaga,
    deleteTaskSaga,
    reorderTaskSaga,
    moveTaskToListSaga
} from '../store/tasks/tasksSlice'
import {
    selectTasksWithOptimistic,
    selectTasksLoading,
    selectTasksError
} from '../store/tasks/tasksSelectors'

export interface Task {
    id: string
    workspaceId: string
    projectId: string
    listId: string
    number: number
    name: string
    description: unknown
    ownerId: string | null
    apiKeyName?: string | null
    assigneeId: string | null
    statusId: string
    // The status the task held immediately before it was completed. Set when a task moves into a
    // Done status (checkbox or dropdown); read to restore that status on un-complete (SCD-982).
    previousStatusId?: string | null
    labels: string[]
    order: number
    isCompleted: boolean
    createdAt: any
    updatedAt: any
    pending?: boolean
    // Maintained server-side by the comment triggers; used to gate the comment hover prefetch.
    commentCount?: number
}

interface TasksContextValue {
    tasks: Task[]
    loading: boolean
    error: string | null
    createTask: (listId: string, name: string) => Promise<string | null>
    createTasks: (listId: string, names: string[]) => void
    updateTask: (taskId: string, data: Partial<Pick<Task, 'name' | 'description' | 'assigneeId' | 'statusId' | 'labels' | 'order' | 'isCompleted'>>) => Promise<void>
    deleteTask: (taskId: string) => Promise<void>
    reorderTask: (taskId: string, newOrder: number) => Promise<void>
    moveTaskToList: (taskId: string, targetListId: string, newOrder: number) => Promise<void>
}

const TasksContext = createContext<TasksContextValue | null>(null)

export const useTasks = () => {
    const context = useContext(TasksContext)
    if (!context) {
        throw new Error('useTasks must be used within TasksProvider')
    }
    return context
}

interface TasksProviderProps {
    children: ReactNode
    projectId?: string
}

export const TasksProvider = ({ children, projectId }: TasksProviderProps) => {
    const dispatch = useAppDispatch()
    const { workspace } = useWorkspace()
    const workspaceId = workspace?.id
    const auth = useAppSelector(state => state.auth)
    const userId = auth?.id

    const allTasks = useAppSelector(selectTasksWithOptimistic)
    const tasks = useMemo(
        () => projectId ? allTasks.filter(t => t.projectId === projectId) : allTasks,
        [allTasks, projectId]
    )
    const loading = useAppSelector(selectTasksLoading)
    const error = useAppSelector(selectTasksError)

    const createTask = useCallback(async (listId: string, name: string): Promise<string | null> => {
        if (!workspaceId || !projectId) {
            console.error('Cannot create task: missing workspaceId or projectId')
            return null
        }
        dispatch(createTaskSaga({ listId, name, projectId, workspaceId, userId: userId || '' }))
        return null
    }, [dispatch, projectId, workspaceId, userId])

    const createTasks = useCallback((listId: string, names: string[]): void => {
        if (!workspaceId || !projectId) {
            console.error('Cannot create tasks: missing workspaceId or projectId')
            return
        }
        dispatch(createTasksSaga({ names, listId, projectId, workspaceId, userId: userId || '' }))
    }, [dispatch, projectId, workspaceId, userId])

    const updateTask = useCallback(async (taskId: string, data: Partial<Pick<Task, 'name' | 'description' | 'assigneeId' | 'statusId' | 'labels' | 'order' | 'isCompleted'>>): Promise<void> => {
        dispatch(updateTaskSaga({ taskId, data }))
    }, [dispatch])

    const deleteTask = useCallback(async (taskId: string): Promise<void> => {
        dispatch(deleteTaskSaga(taskId))
    }, [dispatch])

    const reorderTask = useCallback(async (taskId: string, newOrder: number): Promise<void> => {
        dispatch(reorderTaskSaga({ taskId, newOrder }))
    }, [dispatch])

    const moveTaskToList = useCallback(async (taskId: string, targetListId: string, newOrder: number): Promise<void> => {
        dispatch(moveTaskToListSaga({ taskId, targetListId, newOrder }))
    }, [dispatch])

    const value: TasksContextValue = {
        tasks,
        loading,
        error,
        createTask,
        createTasks,
        updateTask,
        deleteTask,
        reorderTask,
        moveTaskToList
    }

    return (
        <TasksContext.Provider value={value}>
            {children}
        </TasksContext.Provider>
    )
}
